import { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmissionRecords } from "reduxStore/actions/emissionRecordActions";
import { useTheme } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { checkRoles } from "utils/permissions";

export const useEmissionRecordsGrid = () => {
  const dispatch = useDispatch();

  const { palette } = useTheme();
  const backgroundAlt = palette.background.alt;
  const primaryLight = palette.primary.light;
  const primaryDark = palette.primary.dark;

  const user = useSelector((state) => state.user || {});
  const { isSystemAdmin } = checkRoles(user);

  const [scope, setScope] = useState("all"); // "all" | "org" | "system"

  const records = useSelector((s) => s.emissionRecords.data || []);
  const total = useSelector((s) => s.emissionRecords.total || 0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentIngredient, setCurrentIngredient] = useState(null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    source_product_name: true,
    origin_region: true,
    applicable_region: true,
    farmGate: false,
    processingTransport: false,
    totalEmissions: true,
    landUseChange: false,
    processing: false,
    packaging: false,
    transport: false,
    feed: false,
    farm: false,
    plantGateFeedOnly: false,
    data_quality_score: true,
    scope: true,
    edit: true,
  });

  const handleOpen = useCallback(() => {
    setEditMode(false);
    setCurrentIngredient(null);
    setOpen(true);
  }, []);

  const handleEditOpen = useCallback((ingredient) => {
    setEditMode(true);
    setCurrentIngredient(ingredient);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  // columns definition
  const columns = useMemo(() => {
    const baseColumns = [
      { field: "source_product_name", headerName: "Raw Ingredient", flex: 2 },
      { field: "origin_region", headerName: "Origin", flex: 1 },
      { field: "applicable_region", headerName: "Applicable Region", flex: 1 },

      {
        field: "landUseChange",
        headerName: "Land-use Change (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.land_use_change;
          return v != null ? v.toFixed(2) : "";
        },
      },
      {
        field: "processing",
        headerName: "Processing (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.processing;
          return v != null ? v.toFixed(2) : "";
        },
      },
      {
        field: "packaging",
        headerName: "Packaging (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.packaging;
          return v != null ? v.toFixed(2) : "";
        },
      },
      {
        field: "transport",
        headerName: "Transport (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.transport;
          return v != null ? v.toFixed(2) : "";
        },
      },
      {
        field: "feed",
        headerName: "Feed (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.feed;
          return v != null ? v.toFixed(2) : "";
        },
      },
      {
        field: "farm",
        headerName: "Farm (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.farm;
          return v != null ? v.toFixed(2) : "";
        },
      },
      {
        field: "plantGateFeedOnly",
        headerName: "C→Plant Gate (feed only) (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params.row.emissions?.cradle_to_plant_gate_feed_only;
          return v != null ? v.toFixed(2) : "";
        },
      },

      // Farm-gate emissions
      {
        field: "farmGate", // simple ID
        headerName: "Farm-gate (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params?.row?.emissions?.cradle_to_farm_gate;
          return v != null ? v.toFixed(2) : "";
        },
      },

      // Cradle→Processing+Transport
      {
        field: "processingTransport",
        headerName: "C→P+T (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v =
            params?.row?.emissions?.cradle_to_processing_gate_plus_transport;
          return v != null ? v.toFixed(2) : "";
        },
      },
      // Total emissions
      {
        field: "totalEmissions",
        headerName: "Emissions Factor (kg CO₂e)",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const e = params.row.emissions || {};
          let total;
          // 1) Prefer the full C→P+T if present...
          if (e.cradle_to_processing_gate_plus_transport != null) {
            total = e.cradle_to_processing_gate_plus_transport;
          }
          // 2) …else farm-gate…
          else if (e.cradle_to_farm_gate != null) {
            total = e.cradle_to_farm_gate;
          }
          // 3) …else sum up all numeric stage values:
          else {
            total = Object.values(e).reduce(
              (sum, v) => (typeof v === "number" ? sum + v : sum),
              0
            );
          }
          return total != null ? total.toFixed(2) : "";
        },
      },

      // Data-quality
      isSystemAdmin && {
        field: "data_quality_score",
        headerName: "Data Quality",
        type: "number",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => {
          const v = params?.row?.data_quality_score;
          return v != null ? v.toFixed(2) : "n/a";
        },
      },
      {
        field: "scope",
        headerName: "Scope",
        flex: 1,
        renderCell: (params) =>
          params.row.organisation
            ? `${params.row.organisation.name} only`
            : "System-wide",
      },

      {
        field: "edit",
        headerName: "Edit",
        flex: 0.5,
        renderCell: (params) => {
          // if (params.row.type !== "product") return null;
          return (
            <button
              onClick={() => handleEditOpen(params.row)}
              style={{ backgroundColor: "transparent", border: "none" }}
            >
              <EditOutlined sx={{ color: primaryDark }} />
            </button>
          );
        },
      },
    ];
    return baseColumns.filter(Boolean);
  }, [primaryDark, handleEditOpen, isSystemAdmin]);

  // wire up row data and total
  const rows = useMemo(() => records, [records]);

  // debounce search -> setSearch
  useEffect(() => {
    const h = setTimeout(() => {
      setPage(1);
      setSearch(searchInput);
    }, 500);
    return () => clearTimeout(h);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [sort, scope]);

  // 3) Fetch whenever page, pageSize, sort or search change
  useEffect(() => {
    dispatch(fetchEmissionRecords({ page, pageSize, sort, search, scope }));
  }, [dispatch, page, pageSize, sort, search, scope]);

  // 4) Build the DataGrid sortModel
  const sortModel = useMemo(() => {
    if (!sort) return [];
    const m = sort.match(/^(.*?):(asc|desc)$/);
    return m ? [{ field: m[1], sort: m[2] }] : [];
  }, [sort]);

  // 5) Handle user clicking a column header
  const onSortModelChange = (model) => {
    if (model[0]) {
      setSort(`${model[0].field}:${model[0].sort}`);
    } else {
      setSort("");
    }
  };

  return {
    backgroundAlt,
    primaryLight,
    rows,
    columns,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortModel,
    onSortModelChange,
    searchInput,
    setSearchInput,
    setSearch,
    open,
    editMode,
    currentIngredient,
    handleOpen,
    handleClose,
    setOpen,
    columnVisibilityModel,
    setColumnVisibilityModel,
    isSystemAdmin,
    scope,
    setScope,
  };
};
